import { Fragment } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useMe } from '../hooks';
import { Icon } from '../shared/Icon';
import type { IconName } from '../shared/icons';
import type { ApiError } from '../api';

/**
 * Каркас приложения внутри тенанта: сайдбар кабинета плюс рабочая область.
 * Тенант берётся из URL, а не из состояния, — тогда ссылка на задачу или очередь открывается
 * в нужном трекере без предварительного выбора.
 */
export function AppLayout() {
  const { tenantId = '' } = useParams();
  const navigate = useNavigate();
  const me = useMe();

  const tenant = me.data?.tenants.find((t) => t.id === tenantId);

  if (me.isLoading) return <div className="state">Загрузка…</div>;
  if (me.isError) {
    // 401 означает переход ко входу, а не поломку.
    return (
      <div className="state">
        {(me.error as ApiError).status === 401 ? 'Переходим ко входу…' : 'Сервис трекера недоступен'}
      </div>
    );
  }
  if (!tenant) {
    return (
      <div className="entry-page">
        <div className="entry-wrap">
          <div className="card">
            <div className="state-title">Трекер недоступен</div>
            <p className="muted">
              Возможно, вас исключили из него или ссылка ведёт в чужой трекер.
            </p>
            <div className="state-actions">
              <button className="btn btn-primary" onClick={() => navigate('/')}>
                К выбору трекера
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // end у «Всех задач»: карточка задачи живёт под /issues/, и без него пункт подсвечивался бы
  // даже когда задачу открыли из очереди.
  const nav: Array<{
    section: string;
    items: Array<{ to: string; label: string; icon: IconName; end?: boolean }>;
  }> = [
    {
      section: 'Работа',
      items: [
        { to: `/t/${tenantId}/queues`, label: 'Очереди', icon: 'sitemap' },
        { to: `/t/${tenantId}/issues`, label: 'Все задачи', icon: 'checkup', end: true },
        { to: `/t/${tenantId}/boards`, label: 'Доски', icon: 'dashboard' },
      ],
    },
    {
      section: 'Трекер',
      items: [{ to: `/t/${tenantId}/settings`, label: 'Настройки', icon: 'settings' }],
    },
  ];

  const logout = async () => {
    const res = await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    const body = (await res.json().catch(() => ({}))) as { redirectTo?: string };
    window.location.href = body.redirectTo ?? '/';
  };

  return (
    <div className="app-layout">
      <nav className="sidebar">
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">
            <Icon name="activity" size={18} />
          </div>
          <div className="sidebar-logo-text">Трекер</div>
        </div>

        {nav.map((group) => (
          <Fragment key={group.section}>
            <div className="sidebar-section">{group.section}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              >
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            ))}
          </Fragment>
        ))}

        <div className="sidebar-footer">
          {/* Переключатель трекеров: у человека их обычно два — свой и компании. */}
          {me.data!.tenants.length > 1 && (
            <select
              className="input"
              value={tenantId}
              onChange={(event) => navigate(`/t/${event.target.value}/queues`)}
              aria-label="Трекер"
            >
              {me.data!.tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
          <div className="sidebar-user">
            <div className="user-avatar">{initials(tenant.name)}</div>
            <div style={{ minWidth: 0 }}>
              <div className="user-name">{tenant.name}</div>
              <div className="user-id">{roleLabel(tenant.myRole)}</div>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={logout}>
            <Icon name="logout" />
            Выйти
          </button>
        </div>
      </nav>

      <div className="main-area">
        <main className="page-content">
          {tenant.status === 'SUSPENDED' && (
            <div className="info-banner amber">
              Трекер приостановлен: данные видны, но изменить их нельзя.
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const roleLabel = (role: string): string =>
  ({ OWNER: 'владелец', ADMIN: 'администратор', MEMBER: 'участник', VIEWER: 'наблюдатель' })[role] ?? role;
